import { Component, OnInit, computed, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { InternalTransferProxyService } from '../../../proxies/internal-transfer.proxy.service';
import { InternalTransfer, InternalTransferStatus } from '../../../models/internal-transfer/internal-transfer';
import { EnumService } from '../../../services/enum.service';

/**
 * Internal transfers list (#5089).
 *
 * <p>Defaults to the actionable set — drafts and anything in transit — rather than everything. The row
 * someone opens this page looking for is almost always one awaiting confirmation, and a completed transfer
 * is history. The in-transit count is surfaced on its own chip because that number is the working queue:
 * every one of those is money sitting in a clearing account with nobody yet saying it arrived.</p>
 */
@Component({
  selector: 'app-internal-transfers',
  templateUrl: './internal-transfers.component.html',
  styleUrl: './internal-transfers.component.scss',
})
export class InternalTransfersComponent implements OnInit {
  readonly InternalTransferStatus = InternalTransferStatus;

  readonly transfers = signal<InternalTransfer[]>([]);
  readonly isLoading = signal(0);

  private statusLabels: Record<number, string> = {};
  private currencyLabels: Record<number, string> = {};

  /** The working queue by default. Completed transfers are history and stay one click away. */
  readonly actionableOnlyControl = new FormControl<boolean>(true, { nonNullable: true });
  private readonly actionableOnly = toSignal(this.actionableOnlyControl.valueChanges, { initialValue: true });

  readonly searchControl = new FormControl<string>('', { nonNullable: true });
  private readonly search = toSignal(this.searchControl.valueChanges, { initialValue: '' });

  readonly inTransitCount = computed(() => this.transfers().filter((t) => t.status === InternalTransferStatus.IN_TRANSIT).length);
  readonly draftCount = computed(() => this.transfers().filter((t) => t.status === InternalTransferStatus.DRAFT).length);

  readonly filteredTransfers = computed(() => {
    const term = this.search().trim().toLowerCase();
    let rows = this.transfers();

    if (this.actionableOnly()) {
      rows = rows.filter((t) => t.status === InternalTransferStatus.DRAFT || t.status === InternalTransferStatus.IN_TRANSIT);
    }
    if (term) {
      rows = rows.filter((t) =>
        [t.number, t.fromAccountCode, t.toAccountCode, t.cashRequest?.number, t.notes].some((field) =>
          (field ?? '').toLowerCase().includes(term),
        ),
      );
    }
    return rows;
  });

  constructor(
    private router: Router,
    private service: InternalTransferProxyService,
    private enumService: EnumService,
  ) {
    this.statusLabels = this.enumService.getEnum('InternalTransferStatus').values as Record<number, string>;
    this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;
  }

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.isLoading.update((n) => n + 1);
    // Paged high rather than unbounded: a company runs a handful of transfers a week, and one page keeps
    // the client-side status filter honest — filtering a partial page would hide rows that do match.
    this.service.search({}, 1, 500, true, 'TransferDate', -1).subscribe({
      next: (response) => {
        this.transfers.set(response.data?.items ?? []);
        this.isLoading.update((n) => n - 1);
      },
      error: (error) => {
        this.transfers.set([]);
        this.isLoading.update((n) => n - 1);
        console.error('Error loading internal transfers:', error);
      },
    });
  }

  openTransfer(transfer: InternalTransfer): void {
    this.router.navigate(['/internal-transfer-details'], { queryParams: { id: transfer.id } });
  }

  /** A transfer always starts from the account sending the money, so creation begins on Bank Accounts. */
  startNewTransfer(): void {
    this.router.navigate(['/bank-accounts']);
  }

  /** What actually landed. Null until the incoming leg exists — an in-transit transfer has no answer yet. */
  receivedAmount(transfer: InternalTransfer): number | null {
    const leg = transfer.incomingLeg;
    if (!leg) return null;
    const gross = leg.to?.amount ?? 0;
    const fees = (leg.fees ?? []).reduce((sum, f) => sum + (f.amount || 0), 0);
    return gross - fees;
  }

  statusLabel(status: number): string {
    return this.statusLabels[status] ?? '';
  }

  currencyLabel(currency: number | null | undefined): string {
    if (currency === null || currency === undefined) return '';
    return this.currencyLabels[currency] ?? '';
  }

  statusSeverity(status: number): 'success' | 'warn' | 'danger' | 'info' | 'secondary' {
    switch (status) {
      case InternalTransferStatus.RECEIVED:
        return 'success';
      case InternalTransferStatus.IN_TRANSIT:
        return 'warn';
      case InternalTransferStatus.RETURNED:
        return 'danger';
      case InternalTransferStatus.CANCELLED:
        return 'secondary';
      default:
        return 'info';
    }
  }
}
